import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Page, Card, DataTable, Button, ButtonGroup, TextField, Select, FormLayout,
    Banner, Toast, Frame,
} from '@shopify/polaris';
import { authenticatedFetch, useAuth } from '../AuthComponent';

import { hasRole } from '../constants/roles';
// Human labels for store keys — the /api/rankings/locations catalog
// returns raw keys; we display these instead. Aggregate entries carry
// store_key="multi", handled separately.
const STORE_LABEL = {
    soccerpost: 'Soccer Post',
    surf:       'Surf',
    u90:        'Upper 90',
    b2b:        'B2B',
    rush:       'Rush',
    citysports: 'City Sports',
    stefans:    "Stefan's",
    multi:      'All Stores',
};

export default function RankingsPortal() {
    const { user } = useAuth();
    const [tokens, setTokens] = useState([]);
    const [locations, setLocations] = useState([]);
    const [error, setError] = useState('');
    const [form, setForm] = useState({
        locationKey: '', expires_in_days: '90',
        // 2026-08-27: which leaderboard columns the TV shows (1, 2, or all 3).
        roles: ['picking', 'pressing', 'packing'],
    });
    const [toast, setToast] = useState(null);

    const load = useCallback(async () => {
        try {
            const [tokRes, locRes] = await Promise.all([
                authenticatedFetch('/api/rankings/tokens'),
                authenticatedFetch('/api/rankings/locations'),
            ]);
            if (!tokRes.ok) throw new Error(await tokRes.text());
            if (!locRes.ok) throw new Error(await locRes.text());
            setTokens(await tokRes.json());
            setLocations(await locRes.json());
            setError('');
        } catch (e) {
            setError(String(e.message || e));
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Poll the token list every 10s so the Viewers column stays live.
    useEffect(() => {
        const id = setInterval(load, 10_000);
        return () => clearInterval(id);
    }, [load]);

    // Build the location Select's options. Groups:
    //   1. Aggregate (Teams Lancaster (All Locations), Tinton Falls (All Locations))
    //   2. Per-store locations, alphabetized
    const locationOptions = useMemo(() => {
        const agg = locations.filter(l => l.kind === 'aggregate');
        const phys = locations.filter(l => l.kind !== 'aggregate');
        const opts = [{ label: '— Choose location —', value: '' }];
        if (agg.length) {
            opts.push({ label: '— Aggregate boards —', value: '', disabled: true });
            for (const a of agg) {
                opts.push({
                    label: `  ${a.name}`,
                    value: `${a.store_key}|${a.shopify_location_id}`,
                });
            }
        }
        // Group physical by store — one header per store
        const byStore = {};
        for (const p of phys) {
            (byStore[p.store_key] = byStore[p.store_key] || []).push(p);
        }
        for (const sk of Object.keys(byStore).sort()) {
            opts.push({ label: `— ${STORE_LABEL[sk] || sk} —`, value: '', disabled: true });
            for (const l of byStore[sk]) {
                opts.push({
                    label: `  ${l.name}`,
                    value: `${sk}|${l.shopify_location_id}`,
                });
            }
        }
        return opts;
    }, [locations]);

    const onGenerate = async () => {
        if (!form.locationKey) return;
        const [store_key, shopify_location_id] = form.locationKey.split('|');
        try {
            const r = await authenticatedFetch('/api/rankings/tokens', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    store_key, shopify_location_id,
                    expires_in_days: parseInt(form.expires_in_days, 10) || 90,
                    roles: form.roles,
                }),
            });
            if (!r.ok) throw new Error((await r.json()).detail || 'Create failed');
            const data = await r.json();
            const displayUrl = `${window.location.origin}/rankings/display/${data.token}`;
            await navigator.clipboard.writeText(displayUrl).catch(() => {});
            setToast(`URL copied to clipboard — ${data.location_name}`);
            setForm({ ...form, locationKey: '' });
            await load();
        } catch (e) {
            setError(String(e.message || e));
        }
    };

    const onCopy = async (t) => {
        if (!t.token) { setError('No token available to copy'); return; }
        const displayUrl = `${window.location.origin}/rankings/display/${t.token}`;
        try {
            await navigator.clipboard.writeText(displayUrl);
            setToast(`URL copied — ${t.location_name}`);
        } catch (e) {
            setError('Clipboard write failed');
        }
    };

    const onRevoke = async (id) => {
        if (!window.confirm('Revoke this token? The TV will stop working within 5 seconds.')) return;
        try {
            const r = await authenticatedFetch(`/api/rankings/tokens/${id}/revoke`, { method: 'POST' });
            if (!r.ok) throw new Error(await r.text());
            await load();
        } catch (e) { setError(String(e.message || e)); }
    };

    const onDelete = async (id) => {
        if (!window.confirm('DELETE this token permanently? It disappears from this list and any TV holding it will 401 on its next poll.')) return;
        try {
            const r = await authenticatedFetch(`/api/rankings/tokens/${id}`, { method: 'DELETE' });
            if (!r.ok) throw new Error(await r.text());
            await load();
        } catch (e) { setError(String(e.message || e)); }
    };

    if (!hasRole(user, ['admin'])) {
        return <Page title="Rankings"><Banner tone="critical">Admin only.</Banner></Page>;
    }

    const rows = tokens.map(t => {
        const actions = (
            // Plain flex row — Polaris ButtonGroup flex-wraps its children,
            // which stacked the third button onto a second line (user rule:
            // nothing on this page wraps).
            <div style={{ whiteSpace: 'nowrap', display: 'flex', gap: 8, flexWrap: 'nowrap' }}>
                <Button size="slim" onClick={() => onCopy(t)}>Copy URL</Button>
                {!t.revoked_at && (
                    <Button size="slim" tone="critical" onClick={() => onRevoke(t.id)}>Revoke</Button>
                )}
                <Button size="slim" tone="critical" variant="primary" onClick={() => onDelete(t.id)}>Delete</Button>
            </div>
        );
        // Live viewer count — how many distinct clients have polled this token
        // in the last 60s. Green dot when someone's watching, muted when idle.
        const vc = t.viewer_count || 0;
        const viewers = (
            <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontVariantNumeric: 'tabular-nums',
                color: vc > 0 ? '#00875a' : '#8c9196',
                fontWeight: vc > 0 ? 700 : 500,
            }}>
                <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: vc > 0 ? '#00a63f' : '#c9cccf',
                    boxShadow: vc > 0 ? '0 0 6px rgba(0, 166, 63, 0.7)' : 'none',
                }} />
                {vc}
            </span>
        );
        return [
            t.location_name,
            (t.roles && t.roles.length < 3)
                ? t.roles.map(r => r.charAt(0).toUpperCase() + r.slice(1)).join(' + ')
                : 'All 3',
            STORE_LABEL[t.store_key] || t.store_key,
            viewers,
            t.created_by,
            t.created_at?.slice(0, 10) || '',
            t.expires_at?.slice(0, 10) || '',
            t.revoked_at ? `revoked ${t.revoked_at.slice(0, 10)}` : 'active',
            actions,
        ];
    });

    return (
        <Frame>
            <Page
                fullWidth
                title="Rankings"
                subtitle="TV leaderboard display tokens"
                primaryAction={{
                    content: 'View scoring rules',
                    url: '/rankings-scoring.html',
                    external: true,
                }}
            >
                {error && <Banner tone="critical" onDismiss={() => setError('')}>{error}</Banner>}
                <Card>
                    <div style={{ padding: 16 }}>
                        <FormLayout>
                            <FormLayout.Group>
                                <Select
                                    label="Location"
                                    options={locationOptions}
                                    value={form.locationKey}
                                    onChange={v => setForm({ ...form, locationKey: v })}
                                    helpText={locations.length ? '' : 'Loading locations…'}
                                />
                                <TextField label="Expires in (days)" type="number"
                                           value={form.expires_in_days}
                                           onChange={v => setForm({ ...form, expires_in_days: v })} />
                            </FormLayout.Group>
                            <div>
                                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                                    Columns on this board
                                </div>
                                <div style={{ display: 'flex', gap: 20 }}>
                                    {['picking', 'pressing', 'packing'].map(role => (
                                        <label key={role} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={form.roles.includes(role)}
                                                onChange={() => setForm(f => ({
                                                    ...f,
                                                    roles: f.roles.includes(role)
                                                        ? f.roles.filter(r => r !== role)
                                                        : [...f.roles, role],
                                                }))}
                                            />
                                            {role.charAt(0).toUpperCase() + role.slice(1)}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <ButtonGroup>
                                <Button variant="primary" onClick={onGenerate}
                                        disabled={!form.locationKey || form.roles.length === 0}>
                                    Generate URL
                                </Button>
                            </ButtonGroup>
                        </FormLayout>
                    </div>
                </Card>
                <div style={{ height: 16 }} />
                <Card>
                    {/* 2026-08-27: nothing in this table may wrap (user rule) —
                        Polaris DataTable cells wrap by default. */}
                    <style>{`.rankings-tokens-table .Polaris-DataTable__Cell { white-space: nowrap; }`}</style>
                    <div className="rankings-tokens-table">
                    <DataTable
                        columnContentTypes={['text','text','text','text','text','text','text','text','text']}
                        headings={['Location','Columns','Store','Viewers','Created by','Created','Expires','Status','Actions']}
                        rows={rows}
                    />
                    </div>
                </Card>
                {toast && <Toast content={toast} onDismiss={() => setToast(null)} />}
            </Page>
        </Frame>
    );
}
